import React, { useEffect, useState } from "react";
import { Box } from "@mui/system";
import { useTranslation } from "react-i18next";
import { Button, Typography } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { useDebriefingStyles } from "../../styles/debriefing.styles";
import CheckListService from "../../../services/CheckListService";
import SkillItem from "./SkillItem";
import palette from "../../../common/styles/palette";
import { AiChecklistItem } from "../../../services/sheai/SheAiChecklistTypes";
import secureLocalStorage from "react-secure-storage";

/**
 * A aba Checklist: a Correção que o Avaliador assina.
 *
 * É a lista do Checklist agrupada por Habilidade, com um Veredito por Item. A
 * análise da IA entra como DICA ao lado de cada Veredito (ver `SkillItem`) — o
 * detalhe verboso do agente vive na aba Dados IA.
 */

interface ChecklistItemDados {
  item_id: number | string;
  question?: string;
  scale_value?: string | null;
  /** F3: Detecção + crítico chegam do backend (Checklist::items() select) desde 5893b42. */
  detection?: Record<string, string> | null;
  critical?: number | boolean | null;
}

interface SkillDados {
  skill_id: number | string;
  name?: string;
  items?: ChecklistItemDados[];
}

interface ChecklistResumo {
  theme_name?: string;
  competence_name?: string;
}

interface DebriefingChecklistViewProps {
  stationId: number | string;
  debriefingId: number | string;
  checklistId?: number | string;
  checklistName?: string;
  skills: SkillDados[];
  /** Análise da IA indexada por `item_id`. Vazio = nenhuma análise ainda. */
  iaPorItem: Record<string, AiChecklistItem>;
  /** Estado do agente, para explicar a ausência de dicas sem inventar motivo. */
  iaStatus: string;
  iaMensagem?: string;
  isAppraiser?: boolean;
  /** Leva o professor ao detalhe completo da IA (aba Dados IA). */
  onVerDadosIa?: () => void;
}

export default function DebriefingChecklistView({
  stationId,
  debriefingId,
  checklistId,
  checklistName,
  skills,
  iaPorItem,
  iaStatus,
  iaMensagem,
  isAppraiser,
  onVerDadosIa,
}: DebriefingChecklistViewProps) {
  const { t } = useTranslation();
  const debriefingStyle = useDebriefingStyles();
  const [checklist, setChecklist] = useState<ChecklistResumo>({});
  const perfil = secureLocalStorage.getItem("role") as number | null;
  const isStudent = perfil === 5 && !isAppraiser;

  useEffect(() => {
    if (!checklistId) return;
    CheckListService.get(checklistId)
      .then((resposta) => setChecklist(resposta?.data || {}))
      .catch(() => setChecklist({}));
  }, [checklistId]);

  const temIa = Object.keys(iaPorItem).length > 0;

  return (
    <Box className={debriefingStyle.boxDebriefing} sx={{ flex: 1 }}>
      <Box className={debriefingStyle.titleBoxChecklist} sx={{ gap: 1.5, mb: 2 }}>
        <Typography className={debriefingStyle.titleChecklist} sx={{ fontWeight: 700 }}>
          {t("title_dialog_checklist_view")}
        </Typography>
        <Typography
          sx={{
            color: palette.brand,
            fontWeight: 700,
            fontSize: "1.125rem",
            textTransform: "uppercase",
          }}
        >
          {checklistName}
        </Typography>
      </Box>

      <Box sx={{ mb: 3, fontSize: "0.875rem" }}>
        <Box>
          <strong>{t("title_dialog_checklist_view").toUpperCase()} </strong>
          {checklist?.theme_name}
        </Box>
        <Box>
          <strong>{t("title_dialog_debriefing_checklist_view").toUpperCase()} </strong>
          {checklist?.competence_name}
        </Box>
      </Box>

      {/* Por que não há dica da IA nesta tela. Sem isto a ausência parece bug. */}
      {!isStudent && !temIa && iaStatus && iaStatus !== "finished" && (
        <Box
          sx={{
            mb: 3,
            px: 2,
            py: 1.5,
            borderRadius: "8px",
            backgroundColor: palette.muted,
            color: palette.textMuted,
            fontSize: "0.875rem",
          }}
        >
          {iaStatus === "not_found"
            ? "Sem análise da IA para esta gravação. Ela é disparada automaticamente quando o vídeo fica disponível."
            : iaStatus === "failed"
            ? "A análise da IA falhou. O reprocessamento é feito pela equipe técnica — a correção manual continua valendo."
            : iaMensagem || "Análise da IA em processamento."}
        </Box>
      )}

      {!isStudent && temIa && onVerDadosIa && (
        <Box sx={{ mb: 3 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={onVerDadosIa}
          >
            Ver análise completa
          </Button>
        </Box>
      )}

      {skills.map((skill, indice) => (
        <Box className={debriefingStyle.boxskill} key={skill.skill_id ?? indice}>
          <Box className={debriefingStyle.titleBoxChecklist} sx={{ gap: 1 }}>
            <Typography className={debriefingStyle.titleNumberskill}>
              {String(indice + 1).padStart(2, "0")} /
            </Typography>
            <Typography className={debriefingStyle.titleskill} sx={{ fontWeight: 700 }}>
              {skill.name}
            </Typography>
          </Box>

          {(skill.items || []).map((item) => (
            <SkillItem
              key={item.item_id}
              stationId={stationId}
              debriefingId={debriefingId}
              skillId={skill.skill_id}
              item={item}
              ia={iaPorItem[String(item.item_id)]}
              isAppraiser={isAppraiser}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}
